import { ParameterKeysEnum } from '@src/tokens/orderStrategy';
import Big from 'big.js';

import type { OMSForm, OMSFormOrderParameters } from '@src/providers/OMSContext.types';
import type { FullOrderRequest } from '@src/providers/orders.types';
import {
  ACTION,
  AllocationValueTypeEnum,
  calcDateFromDuration,
  canParseAsDuration,
  convertReceivedAllocationForUI,
  DateField,
  DateTimeDurationPickerValueType,
  DecisionStatusEnum,
  ErrorActionEnum,
  formattedDateForSubscription,
  formatToBackendDuration,
  getTypedKeys,
  includesEnumValue,
  isDuration,
  isOrderComplete,
  isOrderPending,
  logger,
  OrdStatusEnum,
  ParameterTypeEnum,
  ParentOrderTypeEnum,
  parseDate,
  parseDuration,
  RequestSourceEnum,
  TimeInForceEnum,
  useStrategiesContext,
  type AllocationBE,
  type DateTimeDurationPickerValue,
  type ExecutionReport,
  type Order,
  type OrderStrategy,
  type Parameter,
  type Security,
  type UpperCased,
} from '@talos/kyoko';
import { upperFirst } from 'lodash-es';
import { useCallback } from 'react';
import { useOrderAccess, type OrderAccessEntity } from '../hooks/useOrderAccess';
import { useRoleAuth } from '../hooks/useRoleAuth';
import type { AvaAcceptQuoteRequest } from '../providers/quotes.types';

interface FormatParameterArgs<TArgType> {
  parameters: UpperCased<OMSFormOrderParameters>;
  key: TArgType;
  param: Parameter | undefined;
}

/**
 * Parses a string as a strict integer (no decimals, no trailing non-digits).
 * Used for Integer strategy parameters (e.g. NumPriceLevels) from CSV import and validation.
 * @returns The parsed integer, or null if the string is not a valid integer representation.
 */
export function parseStrictIntegerString(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const n = Number(trimmed);
  return !Number.isNaN(n) && Number.isInteger(n) ? n : null;
}

/**
 * If EndTime field is in Duration mode, converts to Duration parameter.
 * Used by OrderService and SalesOrderService when building request parameters.
 * Mutates the params object in place.
 */
export function applyEndTimeDurationToParams(endTimeField: unknown, params: Record<string, string | undefined>): void {
  if (!(endTimeField instanceof DateField)) {
    return;
  }
  const time: DateTimeDurationPickerValue | undefined = endTimeField.dateTimeDuration;
  if (time?.type === DateTimeDurationPickerValueType.Duration) {
    params[ParameterKeysEnum.Duration] = formatToBackendDuration(time.value);
    delete params[ParameterKeysEnum.EndTime];
  }
}

const capitalizeParameters = ({
  parameters,
}: {
  parameters: OMSFormOrderParameters;
}): UpperCased<OMSFormOrderParameters> => {
  getTypedKeys(parameters || {}).forEach(key => {
    const value = parameters[key];
    delete parameters[key];
    parameters[upperFirst(key) as keyof typeof parameters] = value;
  });
  return parameters;
};

const removeUnusedParameters = ({
  parameters,
  selectedParameters,
  allowedParameters,
}: {
  parameters: UpperCased<OMSFormOrderParameters>;
  selectedParameters?: Parameter[];
  /* some parameters are added during import on the client side which selected strategy does not have, we don't want them to be wiped out, ex Duration */
  allowedParameters: string[];
}) => {
  getTypedKeys(parameters).forEach(key => {
    if (parameters[key] === '' || !selectedParameters?.map(p => p.Name)?.includes(key)) {
      if (!allowedParameters.includes(key)) {
        delete parameters[key];
      }
    }
  });
  return parameters;
};

/**
 * Removes parameters that belong to a section whose primary/controlling parameter is "Disabled"
 * or not set.
 *
 * Note: at this point parameter keys have already been capitalised to match Parameter.Name.
 */
const removeDisabledSectionParameters = ({
  parameters,
  selectedStrategy,
}: {
  parameters: UpperCased<OMSFormOrderParameters>;
  selectedStrategy?: OrderStrategy;
}) => {
  if (!selectedStrategy) {
    return;
  }

  getTypedKeys(parameters).forEach(key => {
    const param = selectedStrategy.Parameters.find(p => p.Name === key);
    if (!param) {
      return;
    }

    if (param.isInDisabledSection(selectedStrategy, name => parameters[name as keyof typeof parameters])) {
      delete parameters[key];
    }
  });
};

const dividePercentParameter = ({ parameters, key, param }: FormatParameterArgs<string>) => {
  if (param?.Type === ParameterTypeEnum.Percent) {
    const paramKey = key as keyof typeof parameters;
    const percentValue = parameters[paramKey] as string;
    parameters[paramKey] = Big(percentValue).div(100).toFixed();
  }
};

const formatDateParameter = ({
  parameters,
  key,
  param,
  enableDurationSubmission = false,
}: FormatParameterArgs<typeof ParameterKeysEnum.StartTime | typeof ParameterKeysEnum.EndTime | string> & {
  enableDurationSubmission?: boolean;
}) => {
  let isDurationString = false;

  if (key === ParameterKeysEnum.StartTime || key === ParameterKeysEnum.EndTime) {
    const rawParamValue = parameters[key];
    switch (typeof rawParamValue) {
      case 'string':
        isDurationString = canParseAsDuration(rawParamValue);
        if (key === ParameterKeysEnum.EndTime && isDurationString) {
          if (enableDurationSubmission) {
            // Normalize duration: convert days to hours and remove spaces (backend doesn't support days or spaces)
            parameters[ParameterKeysEnum.Duration] = formatToBackendDuration(parseDuration(rawParamValue));
            delete parameters[key];
          } else {
            // PR #7022 (https://github.com/talostrading/Ava-UI/pull/7022): flag off → convert duration to date and send as EndTime (e.g. non-staged import)
            const durationObj = parseDuration(rawParamValue);
            parameters[key] = formattedDateForSubscription(calcDateFromDuration(durationObj, parseDate()));
          }
        } else {
          parameters[key] = formattedDateForSubscription(rawParamValue);
        }
        break;
      case 'undefined':
        break;
      default:
        // could be a date or duration object
        if (!rawParamValue) {
          break;
        }
        if (isDuration(rawParamValue)) {
          if (key === ParameterKeysEnum.EndTime && enableDurationSubmission) {
            // Send Duration instead of EndTime
            parameters[ParameterKeysEnum.Duration] = formatToBackendDuration(rawParamValue);
            delete parameters[key];
          } else {
            // For StartTime, or EndTime when flag is off: convert duration to absolute date
            const relativeTo = parseDate();
            parameters[key] = formattedDateForSubscription(calcDateFromDuration(rawParamValue, relativeTo));
          }
        } else {
          parameters[key] = formattedDateForSubscription(rawParamValue ?? undefined);
        }
    }
  }
  if (param?.Type === ParameterTypeEnum.Date) {
    const paramKey = key as keyof typeof parameters;
    return isDurationString
      ? parameters[paramKey]
      : formattedDateForSubscription(parameters[paramKey] as string | undefined);
  }
};

const formatEnumParameter = ({ parameters, key, param }: FormatParameterArgs<string | number>) => {
  if (param?.Type === ParameterTypeEnum.Enum) {
    const paramKey = key as keyof typeof parameters;
    if (typeof parameters[paramKey] === 'string') {
      parameters[paramKey] = parseInt(parameters[paramKey] as string);
    }
  }
};

const formatIntegerParameter = ({ parameters, key, param }: FormatParameterArgs<string | number>) => {
  if (param?.Type === ParameterTypeEnum.Integer) {
    const paramKey = key as keyof typeof parameters;
    if (typeof parameters[paramKey] === 'string') {
      const parsed = parseStrictIntegerString(parameters[paramKey] as string);
      if (parsed !== null) {
        parameters[paramKey] = parsed;
      }
    }
  }
};

export const cleanParametersForOrder = ({
  parameters: currParameters,
  selectedStrategy,
  enableDurationSubmission = false,
}: {
  parameters?: OMSFormOrderParameters;
  selectedStrategy?: OrderStrategy;
  enableDurationSubmission?: boolean;
}): UpperCased<OMSFormOrderParameters> & { ErrorAction: ErrorActionEnum } => {
  // Don't modify original parameters obj
  // Capitalize Parameters
  const parameters = capitalizeParameters({ parameters: { ...currParameters } });
  const selectedParameters = selectedStrategy?.Parameters;

  // Cleared EndTime/Duration must not be sent as ''; treat as initial state (omit)
  if (parameters[ParameterKeysEnum.Duration] === '') {
    delete parameters[ParameterKeysEnum.Duration];
  }

  // When flag/Staged, allow Duration so import-set Duration (OrderImport) survives cleaning
  removeUnusedParameters({
    parameters,
    selectedParameters,
    allowedParameters: enableDurationSubmission ? [ParameterKeysEnum.Duration] : [],
  });

  removeDisabledSectionParameters({ parameters, selectedStrategy });

  getTypedKeys(parameters).forEach(key => {
    const param = selectedParameters?.find(p => p.Name === key);

    // Fix Percentage Parameters From 0-100 to Value Between 0-1
    dividePercentParameter({ parameters, key, param });
    formatDateParameter({ parameters, key, param, enableDurationSubmission });
    formatEnumParameter({ parameters, key, param });
    formatIntegerParameter({ parameters, key, param });
  });
  // [ch23616] UI to send ErrorAction=Pause on all orders
  // This will later be removed and set from the orderStrategy subscription
  return { ...parameters, ErrorAction: ErrorActionEnum.Pause };
};

export const prepareAllocationOrderForUI = <T extends { Allocation?: AllocationBE }>(
  data: T
): T & Pick<OMSForm, 'subAccountAllocations' | 'allocationValueType'> => {
  const order = { ...data };
  const allocation = order?.Allocation;

  if (!allocation?.ValueType || !allocation.Allocations) {
    return {
      ...order,
      subAccountAllocations: [],
      allocationValueType: AllocationValueTypeEnum.Percentage,
    };
  }

  const { subAccountAllocations, allocationValueType } = convertReceivedAllocationForUI(allocation);
  return { ...order, subAccountAllocations, allocationValueType };
};

export function ensureFloat(n?: number | string): number {
  if (n == null) {
    return 0.0;
  }
  if (typeof n === 'number') {
    return n;
  }
  return parseFloat(n);
}

export function assertOrderCurrency(order: FullOrderRequest | AvaAcceptQuoteRequest, security: Security): boolean {
  const isValid = order.Currency
    ? order.Currency === security.BaseCurrency || order.Currency === security.QuoteCurrency
    : true;
  if (!isValid) {
    logger.error(new Error(`Wrong currency ${order.Currency} in order for symbol ${order.Symbol}`));
  }
  return isValid;
}

export function useAssertOrderModifiable() {
  const { isAuthorized } = useRoleAuth();
  const checkWriteAccessToOrder = useOrderAccess();
  const { strategiesByName } = useStrategiesContext();

  return useCallback(
    (
      executionReportLike: Pick<
        ExecutionReport,
        'OrdStatus' | 'Strategy' | 'ParentOrderID' | 'ParentOrderType' | 'RequestSource' | 'DecisionStatus'
      > &
        OrderAccessEntity
    ) => {
      if (!isAuthorized(ACTION.SUBMIT_ORDER)) {
        return false;
      }
      if (isOrderPending(executionReportLike.OrdStatus)) {
        return false;
      }
      if (isOrderComplete(executionReportLike.OrdStatus)) {
        return false;
      }
      if (executionReportLike.RequestSource === RequestSourceEnum.Hedger) {
        return false;
      }
      if (executionReportLike.ParentOrderID) {
        if (
          executionReportLike.ParentOrderType !== ParentOrderTypeEnum.CareOrder &&
          executionReportLike.ParentOrderType !== ParentOrderTypeEnum.WorkingOrder
        ) {
          // If the entity has a ParentOrderID, they are not modifiable unless they are CO/WO parent entities
          return false;
        }
      }
      if (!checkWriteAccessToOrder(executionReportLike)) {
        return false;
      }

      // Check if the strategy allows modification
      if (executionReportLike.DecisionStatus !== DecisionStatusEnum.Staged && executionReportLike.Strategy) {
        const strategy = strategiesByName.get(executionReportLike.Strategy);
        if (strategy && !strategy.canBeModified) {
          return false;
        }
      }

      return true;
    },
    [checkWriteAccessToOrder, isAuthorized, strategiesByName]
  );
}

export function useAssertOrderCancelable() {
  const { isAuthorized } = useRoleAuth();
  const checkWriteAccessToOrder = useOrderAccess();

  return useCallback(
    (order: Order | ExecutionReport) => {
      if (!isAuthorized(ACTION.SUBMIT_ORDER)) {
        return false;
      }
      if (order.OrdStatus === OrdStatusEnum.PendingCancel) {
        return false;
      }
      if (order.TimeInForce !== TimeInForceEnum.GoodTillCancel) {
        return false;
      }
      if (!checkWriteAccessToOrder(order)) {
        return false;
      }
      return true;
    },
    [checkWriteAccessToOrder, isAuthorized]
  );
}

export function useAssertOrderPausable() {
  const checkWriteAccessToOrder = useOrderAccess();

  return useCallback(
    (executionReportLike: Pick<ExecutionReport, 'OrdStatus' | 'DecisionStatus'> & OrderAccessEntity) => {
      if (isOrderComplete(executionReportLike.OrdStatus)) {
        return false;
      }
      // DecisionStatusEnum.SystemPaused is included here as these orders can still be "Paused".
      if (
        !includesEnumValue(
          [
            DecisionStatusEnum.Active,
            DecisionStatusEnum.PendingResume,
            DecisionStatusEnum.Staged,
            DecisionStatusEnum.SystemPaused,
            DecisionStatusEnum.WaitingForStartTime,
            DecisionStatusEnum.WaitingForTrigger,
          ],
          executionReportLike.DecisionStatus
        )
      ) {
        return false;
      }
      if (!checkWriteAccessToOrder(executionReportLike)) {
        return false;
      }

      return true;
    },
    [checkWriteAccessToOrder]
  );
}

export function useAssertOrderResumable() {
  const checkWriteAccessToOrder = useOrderAccess();

  return useCallback(
    (executionReportLike: Pick<ExecutionReport, 'OrdStatus' | 'DecisionStatus'> & OrderAccessEntity) => {
      if (isOrderComplete(executionReportLike.OrdStatus)) {
        return false;
      }
      if (
        !includesEnumValue(
          [
            DecisionStatusEnum.Paused,
            DecisionStatusEnum.PendingPause,
            DecisionStatusEnum.Staged,
            DecisionStatusEnum.SystemPaused,
          ],
          executionReportLike.DecisionStatus
        )
      ) {
        return false;
      }
      if (!checkWriteAccessToOrder(executionReportLike)) {
        return false;
      }

      return true;
    },
    [checkWriteAccessToOrder]
  );
}
